import React, { useState } from "react";
import mondaySdk from "monday-sdk-js";
import { Box, TextField, Button, Loader, Heading, Text } from "@vibe/core";


const monday = mondaySdk();

interface ChatUIProps {
  context: { boardId?: number } | null;
}

const ChatUI: React.FC<ChatUIProps> = ({ context }) => {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setResponse(null);

    try {
      const finalPrompt = `You are a senior product manager. Use the following structure to write a Product Requirements Document (PRD) in plain text for the following product idea:

---

1. Overview 🌐  
(Briefly introduce the product's goal and target users)

2. Pain Points 🛑  
(List the key problems this product solves)

3. Proposed Solution 💡  
(Describe how the product solves those problems, include user flows and features)

4. Functional Requirements 🛠️  
(Enumerate core functionalities)

5. Roadmap & Milestones 🎯  
(Describe phased rollout plan if any)

6. User Stories 👤  
(Provide 2-3 user stories for different user types)

7. Dependencies 🔗  
(Mention any 3rd-party APIs, data sources, or platform limitations)

8. Risks & Assumptions ⚠️  
(Highlight risks and assumptions)

9. Conclusion 🚀  
(Summarize the strategic value)

---

Now, generate a PRD for this idea: "${prompt}"`;


      const res = await fetch("https://prd-ai-agent.onrender.com/api/gpt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: finalPrompt }),
      });
      const data = await res.json();
      const gptText = data.text || "No response.";

    setResponse(gptText);

// Insert plain text into the Workdoc
monday.execute("addDocBlock", {
  type: "normal text",
  content: {
    deltaFormat: [{ insert: gptText + "\n" }]
  }
});


    } catch (err) {
      console.error("Error fetching GPT response:", err);
      if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
    setResponse("⚠️ Could not reach the server. Please check your internet connection or try again shortly.");
  } else {
    setResponse("⚠️ Something went wrong on our end. Please try again later.");
  }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box padding={4} backgroundColor="ui.white" borderRadius={2} style={{ maxWidth: 600, margin: "0 auto" }}>
      <Heading level={3} style={{ marginBottom: 16,  color: "#FFFFFF" }} >
        💬 AI PRD Writer
      </Heading>

      <TextField
        placeholder='e.g. "Create a PRD for an AI recruiting tool"'
        value={prompt}
        onChange={(val) => setPrompt(val)}
        size="large"
        fullWidth
        style={{ marginBottom: 16}}
      />

      <Button onClick={handleSend} disabled={loading} size="large"  style={{ marginTop: "16px" }}>
        {loading ? <Loader size={16} /> : "Generate"}
      </Button>
{loading && (
  <Text size="small" color="text.secondary" style={{ marginTop: 8 }}>
    ✨ Generating your PRD... hang tight!
  </Text>
)}
      {response !== null && (
        <Box marginTop={4} backgroundColor="ui.grey.100" padding={3} borderRadius={2}>
          <Text weight="semibold" style={{ marginBottom: 8, color: "#ffffff" }}>
            Generated PRD:
          </Text>
          <Text style={{ whiteSpace: "pre-wrap", color: "#ffffff"}}>
            {response}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default ChatUI;
